import { useContext } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Box, rem, Tabs, Tooltip } from "@mantine/core";

import {
  IconCurrencyDollar,
  IconDirectionSign,
  IconFiles,
  IconFirstAidKit,
  IconId,
  IconMail,
  IconMicroscope,
  IconPill,
  IconStar,
  Icon as TablerIcon,
} from "@tabler/icons-react";

import { ScrollingTabsList } from "@/components/Tabs";
import { PatientContext } from "@/contexts/patient";
import { ProfileContext } from "@/contexts/profile";
import { DevelopmentFeatureFlags, useFeature } from "@/feature-flags";
import { useIsMobile } from "@/hooks/useBreakpoint";
// Deep imports: the `@/messages` barrel re-exports the lazily loaded Messages and Chat pages.
import UnreadCountIndicator, {
  MAX_DISPLAYED_COUNT,
} from "@/messages/components/UnreadCountIndicator";
import { ClientInboxContext } from "@/messages/contexts/client-inbox";
import useClientInbox from "@/messages/hooks/useClientInbox";
import PermissionGate from "@/permission/components/PermissionGate";
import { LAB_RESULTS_VIEW_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import classes from "./ClientTabs.module.css";

/**
 * `lab-results-view`: disabled for staff without lab results view permissions.
 * `under-development`: staff only, disabled, with a "Feature under development" tooltip.
 */
type ClientTabAccess = "lab-results-view" | "under-development";

interface ClientTabDefinition {
  value: string;
  label: string;
  /** Defaults to `label`. */
  ariaLabel?: string;
  icon: TablerIcon;
  testId: string;
  access?: ClientTabAccess;
}

// On desktop the back row sits behind the front row like folder tabs. Phones get one
// scrolling row, front row first; the active tab is scrolled into view on every change.
const BACK_ROW_TABS: ClientTabDefinition[] = [
  {
    value: "profile",
    label: "Client Profile",
    icon: IconId,
    testId: "patient-tab-profile",
  },
  {
    value: "results",
    label: "Results",
    icon: IconMicroscope,
    testId: "patient-tab-results",
    access: "lab-results-view",
  },
  {
    value: "referrals",
    label: "Referrals",
    icon: IconDirectionSign,
    testId: "patient-tab-referrals",
    access: "under-development",
  },
  { value: "files", label: "Files", icon: IconFiles, testId: "patient-tab-files" },
  { value: "billing", label: "Billing", icon: IconCurrencyDollar, testId: "patient-tab-billing" },
];

// Takes the Referrals slot while the client chart messages flag is on.
const MESSAGES_TAB: ClientTabDefinition = {
  value: "messages",
  label: "Messages",
  icon: IconMail,
  testId: "patient-tab-messages",
};

const FRONT_ROW_TABS: ClientTabDefinition[] = [
  {
    value: "overview",
    label: "Overview",
    icon: IconStar,
    testId: "patient-tab-overview",
  },
  {
    value: "encounters",
    label: "Encounters",
    icon: IconFirstAidKit,
    testId: "patient-tab-encounters",
  },
  {
    value: "pam",
    label: "Problems, Allergies, & Meds",
    ariaLabel: "Problems, Allergies, & Medicine",
    icon: IconPill,
    testId: "patient-tab-pam",
  },
  {
    value: "history",
    label: "Client History",
    icon: IconPill,
    testId: "patient-tab-history",
  },
  {
    value: "assessments-vitals/assessments",
    label: "Assessments & Vitals",
    icon: IconFirstAidKit,
    testId: "patient-tab-assessments-vitals",
  },
];

/**
 * One tab in the client chart, with its access gating and an optional unread badge.
 *
 * @param tab - the tab definition.
 * @param unreadCount - shows a badge and adds the count to the aria-label when above zero. Both
 *   cap the count at the badge's maximum.
 */
function ClientTab({ tab, unreadCount = 0 }: { tab: ClientTabDefinition; unreadCount?: number }) {
  const TabIcon = tab.icon;
  const baseAriaLabel = tab.ariaLabel ?? tab.label;
  const ariaLabel =
    unreadCount > 0
      ? `${baseAriaLabel}, ${Math.min(unreadCount, MAX_DISPLAYED_COUNT)} unread`
      : baseAriaLabel;
  const tabElement = (
    <Tabs.Tab
      value={tab.value}
      aria-label={ariaLabel}
      data-testid={tab.testId}
      className={classes.clientTabs}
      disabled={tab.access === "under-development"}
      leftSection={<TabIcon style={{ width: rem(16), height: rem(16) }} />}
      rightSection={
        unreadCount > 0 ? <UnreadCountIndicator inline count={unreadCount} /> : undefined
      }
    >
      {tab.label}
    </Tabs.Tab>
  );

  switch (tab.access) {
    case "under-development":
      return <Tooltip label="Feature under development">{tabElement}</Tooltip>;
    case "lab-results-view":
      return (
        <PermissionGate mode="disable" requiredPermissions={LAB_RESULTS_VIEW_PERMISSION_FLAGS}>
          {tabElement}
        </PermissionGate>
      );
    default:
      return tabElement;
  }
}

/**
 * The client chart's tab bar and routed page. The Messages tab replaces Referrals for staff when its
 * flag is on, and the routed page shares the client's inbox with it.
 */
export default function ClientTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams() as { clientId: string };
  const activeTab = getActiveTab(location.pathname, clientId);
  const { isPatient } = useContext(ProfileContext);
  const { patient } = useContext(PatientContext);
  const isMobile = useIsMobile();
  const isMessagesTabEnabled =
    useFeature(DevelopmentFeatureFlags.ClientChartMessages) && !isPatient;
  const clientInbox = useClientInbox(patient, isMessagesTabEnabled);

  const isVisible = (tab: ClientTabDefinition) =>
    !(isPatient && tab.access === "under-development");
  const backRowTabs = (
    isMessagesTabEnabled
      ? BACK_ROW_TABS.map((tab) => (tab.value === "referrals" ? MESSAGES_TAB : tab))
      : BACK_ROW_TABS
  ).filter(isVisible);
  const frontRowTabs = FRONT_ROW_TABS.filter(isVisible);

  /** Renders a tab, passing the client's unread count to the Messages tab only. */
  const renderTab = (tab: ClientTabDefinition) => (
    <ClientTab
      tab={tab}
      key={tab.value}
      unreadCount={tab === MESSAGES_TAB ? clientInbox.unreadCount : 0}
    />
  );

  function onTabChange(newTabName: string | null): void {
    navigate(`/clients/${clientId}/${newTabName}`);
  }

  return (
    <Box w="100%" p={{ base: "xs", sm: 16 }}>
      <Tabs value={activeTab} variant="unstyled" classNames={classes} onChange={onTabChange}>
        {isMobile ? (
          <ScrollingTabsList
            className={classes.scrollingList}
            data-testid="patient-tabs-scrolling-list"
          >
            {[...frontRowTabs, ...backRowTabs].map(renderTab)}
          </ScrollingTabsList>
        ) : (
          <>
            <Tabs.List grow className={classes.backList}>
              {backRowTabs.map(renderTab)}
            </Tabs.List>
            <Tabs.List grow className={classes.frontList} data-testid="patient-tabs-front-list">
              {frontRowTabs.map(renderTab)}
            </Tabs.List>
          </>
        )}
        <Box bg="white" className={classes.outletBox}>
          <ClientInboxContext.Provider value={isMessagesTabEnabled ? clientInbox : undefined}>
            <Outlet />
          </ClientInboxContext.Provider>
        </Box>
      </Tabs>
    </Box>
  );
}

// Note: route format must be /clients/:clientId/{tabName}/...
function getActiveTab(pathname: string, clientId: string): string {
  const activeTab = pathname.replace(`/clients/${clientId}/`, "").split("/")[0];
  if (activeTab === "assessments-vitals") {
    // TODO: Figure out a better way to handle this
    return "assessments-vitals/assessments";
  }
  return activeTab;
}
